import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Circle, AlertCircle, XCircle } from "lucide-react";

const StepperNavigation = ({ currentStep, steps, onStepChange, stepStatus }) => {
  const getStepIcon = (step) => {
    const status = stepStatus[step.id];
    
    if (status === "completed") {
      return <CheckCircle2 className="w-5 h-5" />;
    } else if (status === "failed") {
      return <XCircle className="w-5 h-5" />;
    } else if (status === "warning") {
      return <AlertCircle className="w-5 h-5" />;
    } else {
      return <Circle className="w-5 h-5" />;
    }
  };

  const getStepColor = (step) => {
    const status = stepStatus[step.id];
    const isActive = currentStep === step.id;
    
    if (status === "completed") {
      // Only completed/passed checks get light green background
      return "bg-green-50 text-green-700 border-green-300";
    } else if (isActive) {
      // Active step - deep green background with white text
      return "bg-green-700 text-white border-green-700 border-2";
    } else if (status === "failed") {
      // White background for failed, badge will show status
      return "bg-white text-gray-700 border-gray-300";
    } else if (status === "warning") {
      // White background for warning
      return "bg-white text-gray-700 border-gray-300";
    } else {
      // Pending - white background
      return "bg-white text-gray-500 border-gray-300";
    }
  };

  return (
    <div className="flex overflow-x-auto gap-2 md:flex-col md:overflow-visible">
      {steps.map((step, index) => {
        const isActive = currentStep === step.id;
        const isClickable = step.enabled !== false;

        return (
          <div key={step.id} className="relative flex-shrink-0">
            {index < steps.length - 1 && (
              <div className="hidden absolute top-12 left-6 w-0.5 h-8 bg-gray-300 md:block" />
            )}
            <motion.button
              whileHover={isClickable ? { scale: 1.02 } : {}}
              whileTap={isClickable ? { scale: 0.98 } : {}}
              onClick={() => isClickable && onStepChange(step.id)}
              disabled={!isClickable}
              className={`relative flex items-start gap-3 w-full p-4 rounded-lg border-2 transition-all text-left ${getStepColor(
                step
              )} ${isClickable ? "cursor-pointer hover:shadow-md" : "cursor-not-allowed opacity-60"}`}
            >
              <div className="flex-shrink-0 mt-0.5">{getStepIcon(step)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex gap-2 items-center mb-1">
                  <span className={`text-xs font-bold ${isActive ? 'opacity-60' : 'opacity-60'}`}>{step.badge}</span>
                  {isActive && (
                    <span className="px-2 py-0.5 text-xs font-medium text-white bg-white bg-opacity-20 rounded border border-white border-opacity-30">
                      Current
                    </span>
                  )}
                  {stepStatus[step.id] === "failed" && (
                    <span className="px-2 py-0.5 text-xs font-medium text-red-700 bg-red-100 rounded border border-red-300">
                      Failed
                    </span>
                  )}
                </div>
                <h3 className="mb-1 text-sm font-semibold leading-tight">{step.title}</h3>
                <p className={`text-xs leading-tight ${isActive ? 'opacity-80' : 'opacity-70'}`}>{step.description}</p>
              </div>
            </motion.button>
          </div>
        );
      })}
    </div>
  );
};

export default StepperNavigation;
